import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { AccountUpdate } from "src/model/class/general classes/account";
import { ResetPasswordData } from "src/model/class/general classes/reset-password-data";
import { ResetPasswordRequest } from "src/model/class/general classes/reset-password-request";
import { ConsentsType } from "src/components/shared-components/enum/enumLoginRegistrationResponse";
import { TypeIntegration } from "src/model/enum/enumDigitalPlace";
import { ThemeService } from "src/service/theme.service";

@Injectable({
  providedIn: "root",
})
export class AccountService {
  _eventSubscriptions: Array<Subscription> = null;

  constructor(
    private _http: HttpClient,
    private _store: Store,
    private _themeService: ThemeService
  ) {}

  ngOnDestroy() {}

  /**
   * Resend email activation user
   * @param tokenActivation
   */
  public resendEmailActivationUser(tokenActivation, theme) {
    return this._http
      .patch(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/resendactivationuser?code=" +
          tokenActivation +
          "&theme=" +
          theme,
        ""
      )
      .toPromise();
  }

  /**
   * Update consents of user
   * @param consentType
   * @param valueCheck
   * @returns
   */
  public performDPUpdateConsents(
    consentType: ConsentsType,
    valueCheck: boolean
  ) {
    return this._http
      .patch(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/consents",
        {
          typeConsent: consentType,
          value: valueCheck,
        }
      )
      .toPromise();
  }

  /**
   * Send email activation
   * @param email
   * @returns
   */
  public async sendEmailActivation(email: String): Promise<boolean> {
    const bodyRequest = {
      email: email,
    };

    return this._http
      .post<boolean>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/send/mail",
        { ...bodyRequest, theme: this._themeService.applicationTheme }
      )
      .toPromise();
  }

  /**
   *  Create digital place account
   *
   *  @param account Card data for creation
   */
  public async createDigitalPlaceAccount(
    account: AccountUpdate
  ): Promise<unknown> {
    return this._http
      .post<AccountUpdate>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/register",
        { ...account, theme: this._themeService.applicationTheme }
      )
      .toPromise();
  }

  /**
   * Update password of account
   * @param account
   * @returns
   */
  public performDPEditPassword(account: AccountUpdate) {
    return this._http
      .patch<AccountUpdate>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/userpassword",
        account
      )
      .toPromise();
  }

  /**
   * Perform delete account
   * @returns
   */
  public performDPDeleteAccount() {
    return this._http
      .delete(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/delete"
      )
      .toPromise();
  }

  /**
   * Update data of account
   * @param account
   * @returns
   */
  public performDPEditAccount(account: AccountUpdate) {
    return this._http
      .patch<AccountUpdate>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/userdata",
        account
      )
      .toPromise();
  }
  /**
   * Send mail to reset account password
   *
   * @param email Email user who want reset password
   * @returns
   */
  public async performDPAccountResetPasswordRequest(
    email: string
  ): Promise<boolean> {
    const resetPasswordRequest: ResetPasswordRequest = {
      email,
    };

    return this._http
      .post<boolean>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/reset/password/request",
        { ...resetPasswordRequest, theme: this._themeService.applicationTheme }
      )
      .toPromise();
  }

  /**
   * This class handle effective reset password by specific token reset
   *
   * @param token Token which identify request reset password
   * @param password Password to apply to user
   * @returns
   */
  public async performDPAccountResetPassword(
    token: string,
    password: string
  ): Promise<boolean> {
    const resetPasswordRequest: ResetPasswordData = {
      token,
      password,
    };

    return this._http
      .patch<boolean>(
        environment.httpDPAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/reset/password",
        resetPasswordRequest
      )
      .toPromise();
  }

  /**
   * This method contact the "NEST-API" server to conclude the registration procedure of a new user.
   *
   * @param activationCode Activation code received from mail to perform account activation
   * @returns
   */
  public async performDPAccountRegistration(
    serverAWSAPIRoot: string,
    activationCode: string
  ): Promise<boolean> {
    return this._http
      .post<boolean>(
        serverAWSAPIRoot +
          environment.anaciPlatformUser +
          AppSettings.API_URL_ANACI_USER +
          "/activate?code=" +
          activationCode,
        ""
      )
      .toPromise();

    //return true;
  }

  /**
   * Handle saving an external integration (CED House, iCond, ...) for the user
   * @param id of the user
   * @param typeIntegration integration to enable/disable (1 = CED House, 2 = iCond), sent in the body
   * @param isIntegrationEnabled
   * @returns promise
   */
  public saveUserIntegration(
    id: number,
    typeIntegration: TypeIntegration,
    isIntegrationEnabled: boolean
  ): Promise<{ enabled: boolean, typeIntegration: TypeIntegration }> {
    return this._http
      .patch<{ enabled: boolean, typeIntegration: TypeIntegration }>(
        environment.httpServerRoot +
          AppSettings.API_URL_USER +
          `/${id}/integration`,
        { enabled: isIntegrationEnabled, typeIntegration }
      )
      .toPromise();
  }
}
